import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiSend, FiSquare, FiTrash2, FiCpu, FiChevronDown, FiFileText, FiExternalLink } from 'react-icons/fi';
import { sopChat, sopModels } from '../../db/company';

/* ── Keyframe animations ── */
const styleId = 'sop-chat-anim';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes sopFadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes sopPulse { 0%,100% { opacity:.3; } 50% { opacity:1; } }
    @keyframes sopCursor { 0%,100% { opacity:1; } 50% { opacity:0; } }
    .sop-msg-enter { animation: sopFadeUp .25s ease-out both; }
    .sop-dot { animation: sopPulse 1.2s ease-in-out infinite; }
    .sop-dot:nth-child(2) { animation-delay:.2s; }
    .sop-dot:nth-child(3) { animation-delay:.4s; }
    .sop-cursor::after { content:'\\25AE'; animation: sopCursor .8s ease-in-out infinite; margin-left:1px; color:#3B82F6; }
    .sop-chat-scroll::-webkit-scrollbar { width:6px; }
    .sop-chat-scroll::-webkit-scrollbar-track { background:transparent; }
    .sop-chat-scroll::-webkit-scrollbar-thumb { background:#2A3B5E; border-radius:3px; }
  `;
  document.head.appendChild(style);
}

const PROVIDER_COLORS = {
  anthropic: { bg: '#FEF3C7', text: '#92400E', label: 'Claude' },
  azure:     { bg: '#DBEAFE', text: '#1E40AF', label: 'Azure' },
};

export default function SOPChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState('');
  const [sources, setSources] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [activeModel, setActiveModel] = useState('');
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);

  // Load available models
  useEffect(() => {
    (async () => {
      const data = await sopModels();
      if (data?.models) {
        setModels(data.models);
        setSelectedModel(data.default || data.models[0]?.id);
      }
    })();
  }, []);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, streaming, scrollToBottom]);

  const sendMessage = async () => {
    const q = input.trim();
    if (!q || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setInput('');
    setLoading(true);
    setStreaming('');
    setSources([]);
    setActiveModel('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Build history (last 10 turns max)
      const history = messages.slice(-10).filter(m => m.role !== 'system');

      const res = await sopChat(q, selectedModel, history);
      if (!res || !res.body) throw new Error('No response from server');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const payload = trimmed.slice(6);
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.sources) {
              setSources(parsed.sources);
              if (parsed.model) setActiveModel(parsed.model);
            }
            if (parsed.content) {
              accumulated += parsed.content;
              setStreaming(accumulated);
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }

      if (accumulated) {
        setMessages(prev => [...prev, { role: 'assistant', content: accumulated, sources: sources }]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}`, isError: true }]);
      }
    } finally {
      setStreaming('');
      setLoading(false);
      abortRef.current = null;
    }
  };

  const stop = () => { if (abortRef.current) abortRef.current.abort(); };

  const clear = () => {
    stop();
    setMessages([]);
    setSources([]);
    setStreaming('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const modelSpec = models.find(m => m.id === (activeModel || selectedModel));
  const providerStyle = modelSpec ? (PROVIDER_COLORS[modelSpec.provider] || PROVIDER_COLORS.azure) : PROVIDER_COLORS.anthropic;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', maxWidth: 900, margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
        <div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: 0 }}>SOP Chat</h1>
          <p style={{ color: '#64748B', fontSize: 13, margin: '4px 0 0' }}>Ask questions about Strike SOPs and procedures</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Model selector */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowModelMenu(!showModelMenu)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#111B3A', border: '1px solid #1E2A4A', borderRadius: 8,
                padding: '7px 12px', color: '#E2E8F0', fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <FiCpu size={14} />
              <span>{selectedModel || 'Select model'}</span>
              <FiChevronDown size={12} />
            </button>
            {showModelMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4,
                background: '#111B3A', border: '1px solid #1E2A4A', borderRadius: 8,
                zIndex: 10, minWidth: 220, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,.4)',
              }}>
                {models.map(m => {
                  const ps = PROVIDER_COLORS[m.provider] || PROVIDER_COLORS.azure;
                  return (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedModel(m.id); setShowModelMenu(false); }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', border: 'none', background: m.id === selectedModel ? '#1E2A4A' : 'transparent',
                        color: '#E2E8F0', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
                      }}
                    >
                      <span>{m.id}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                        background: ps.bg, color: ps.text,
                      }}>{ps.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button onClick={clear} style={{
            background: '#111B3A', border: '1px solid #1E2A4A', borderRadius: 8,
            padding: '7px 12px', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: 'inherit',
          }}>
            <FiTrash2 size={13} /> Clear
          </button>
        </div>
      </div>

      {/* Message area */}
      <div
        ref={scrollRef}
        className="sop-chat-scroll"
        style={{
          flex: 1, overflowY: 'auto', borderRadius: 12,
          background: '#0D1529', border: '1px solid #1E2A4A', padding: 20,
        }}
      >
        {messages.length === 0 && !loading && (
          <div className="sop-msg-enter" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 80 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28,
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, fontSize: 20, color: '#fff', fontWeight: 700,
            }}>AI</div>
            <p style={{ color: '#64748B', fontSize: 14, textAlign: 'center', maxWidth: 400 }}>
              Ask me about any Strike SOP — safety procedures, equipment operations, compliance checklists, and more.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              {['What PPE is required on site?', 'How do I perform a toolbox talk?', 'Explain the permit-to-work process'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); inputRef.current?.focus(); }}
                  style={{
                    background: '#111B3A', border: '1px solid #1E2A4A', borderRadius: 8,
                    padding: '8px 14px', color: '#94A3B8', fontSize: 12, cursor: 'pointer',
                    fontFamily: 'inherit', transition: 'border-color .15s',
                  }}
                  onMouseEnter={e => e.target.style.borderColor = '#3B82F6'}
                  onMouseLeave={e => e.target.style.borderColor = '#1E2A4A'}
                >{q}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className="sop-msg-enter" style={{
            display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 12,
          }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: 28, height: 28, borderRadius: 14, marginRight: 8, marginTop: 2, flexShrink: 0,
                background: msg.isError ? '#EF4444' : 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#fff', fontWeight: 700,
              }}>AI</div>
            )}
            <div style={{
              maxWidth: '80%', padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user' ? '#3B82F6' : '#111B3A',
              border: msg.role === 'user' ? 'none' : '1px solid #1E2A4A',
            }}>
              <div style={{
                fontSize: 14, lineHeight: '22px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                color: msg.role === 'user' ? '#fff' : (msg.isError ? '#FCA5A5' : '#E2E8F0'),
              }}>{msg.content}</div>
            </div>
          </div>
        ))}

        {/* Streaming response */}
        {loading && streaming && (
          <div className="sop-msg-enter" style={{ display: 'flex', marginBottom: 12 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 14, marginRight: 8, marginTop: 2, flexShrink: 0,
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, color: '#fff', fontWeight: 700,
            }}>AI</div>
            <div style={{
              maxWidth: '80%', padding: '10px 14px', borderRadius: '14px 14px 14px 4px',
              background: '#111B3A', border: '1px solid #1E2A4A',
            }}>
              <div className="sop-cursor" style={{ fontSize: 14, lineHeight: '22px', whiteSpace: 'pre-wrap', color: '#E2E8F0' }}>
                {streaming}
              </div>
            </div>
          </div>
        )}

        {/* Loading dots */}
        {loading && !streaming && (
          <div className="sop-msg-enter" style={{ display: 'flex', marginBottom: 12 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 14, marginRight: 8, marginTop: 2, flexShrink: 0,
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, color: '#fff', fontWeight: 700,
            }}>AI</div>
            <div style={{
              padding: '12px 18px', borderRadius: '14px 14px 14px 4px',
              background: '#111B3A', border: '1px solid #1E2A4A',
              display: 'flex', gap: 5, alignItems: 'center',
            }}>
              <div className="sop-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B82F6' }} />
              <div className="sop-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B82F6' }} />
              <div className="sop-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B82F6' }} />
            </div>
          </div>
        )}
      </div>

      {/* Sources panel */}
      {sources.length > 0 && (
        <div style={{
          marginTop: 8, background: '#111B3A', border: '1px solid #1E2A4A', borderRadius: 10, padding: '10px 14px',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <FiFileText size={12} /> SOURCES
            {activeModel && (
              <span style={{
                marginLeft: 'auto', fontSize: 10, padding: '2px 6px', borderRadius: 4,
                background: providerStyle.bg, color: providerStyle.text, fontWeight: 600,
              }}>{activeModel}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sources.map((s, i) => (
              <div key={i} style={{
                background: '#0D1529', border: '1px solid #1E2A4A', borderRadius: 6,
                padding: '6px 10px', fontSize: 11, color: '#94A3B8', maxWidth: 200,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 4,
              }} title={s.preview}>
                <FiExternalLink size={10} style={{ flexShrink: 0 }} />
                <span style={{ fontWeight: 500, color: '#CBD5E1' }}>{s.file_name}</span>
                <span style={{ color: '#3B82F6' }}>({(s.score * 100).toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', marginTop: 10, flexShrink: 0,
        background: '#111B3A', borderRadius: 12, border: '1px solid #1E2A4A',
        padding: '6px 6px 6px 14px',
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about an SOP..."
          rows={1}
          style={{
            flex: 1, border: 'none', outline: 'none', resize: 'none',
            fontSize: 14, fontFamily: 'inherit', padding: '8px 0',
            lineHeight: '22px', background: 'transparent', color: '#E2E8F0',
            maxHeight: 120, overflowY: 'auto',
          }}
          onInput={e => {
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
          }}
        />
        {loading ? (
          <button onClick={stop} style={{
            background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 14px', marginLeft: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          }}><FiSquare size={14} /> Stop</button>
        ) : (
          <button onClick={sendMessage} style={{
            background: input.trim() ? '#3B82F6' : '#1E3A5F', color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 14px', marginLeft: 6, cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600,
            fontFamily: 'inherit', transition: 'background .2s',
          }}><FiSend size={14} /> Send</button>
        )}
      </div>
    </div>
  );
}
